import { AxiosError } from 'axios';

/**
 * Type guard to check if an error is an AxiosError
 */
export function isAxiosError(error: unknown): error is AxiosError {
  return (
    typeof error === 'object' &&
    error !== null &&
    'isAxiosError' in error &&
    (error as AxiosError).isAxiosError === true
  );
}

/**
 * Extracts a user-friendly error message from various error types.
 * Handles Axios errors, standard Error objects, and unknown error types.
 *
 * @param error - The caught error (unknown type)
 * @param fallbackMessage - Default message if no specific message can be extracted
 * @returns A string error message suitable for display to users
 */
export function getErrorMessage(error: unknown, fallbackMessage = 'An unexpected error occurred'): string {
  // Handle Axios errors with response data
  if (isAxiosError(error)) {
    const responseData = error.response?.data as Record<string, unknown> | undefined;

    // Try common API error message patterns
    if (responseData?.message && typeof responseData.message === 'string') {
      return responseData.message;
    }
    if (responseData?.error && typeof responseData.error === 'string') {
      return responseData.error;
    }
    if (responseData?.errors && Array.isArray(responseData.errors)) {
      return responseData.errors.join(', ');
    }

    // Fall back to Axios error message
    if (error.message) {
      return error.message;
    }
  }

  // Handle standard Error objects
  if (error instanceof Error) {
    return error.message;
  }

  // Handle string errors
  if (typeof error === 'string') {
    return error;
  }

  return fallbackMessage;
}

/**
 * Logs an error with context for debugging while returning a user-friendly message.
 * Use this in catch blocks where you want to log the full error but show a clean message.
 *
 * @param error - The caught error
 * @param context - A string describing where the error occurred (e.g., 'fetching bookings')
 * @param fallbackMessage - Default message for users
 * @returns A user-friendly error message
 */
export function handleError(
  error: unknown,
  context: string,
  fallbackMessage?: string
): string {
  const message = getErrorMessage(error, fallbackMessage || `Error ${context}`);

  // Log full error for debugging (will be stripped in production by babel plugin)
  console.error(`Error ${context}:`, error);

  return message;
}
